import { useMemo, useState } from "react";
import { Ban, Plus, Receipt } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg, inr, today, useCan, useLoad } from "@/lib/api";
import { Empty, ErrorBox, PageHeading } from "@/components/common";
import ExpenseForm, { BillLink } from "@/components/forms/ExpenseForm";

export default function Expenses() {
  const can = useCan();
  const [month, setMonth] = useState(today().slice(0, 7));
  const [show, setShow] = useState(false);
  const { data, error, reload } = useLoad(`/expenses?month=${month}`, []);
  const active = data.filter((e) => e.status === "active");
  const byCat = useMemo(() => Object.entries(active.reduce((m, e) => ({ ...m, [e.category]: (m[e.category] || 0) + e.amount }), {})).sort((a, b) => b[1] - a[1]), [active]);
  const total = active.reduce((s, e) => s + e.amount, 0);
  const cash = active.filter((e) => e.from_drawer).reduce((s, e) => s + e.amount, 0);
  const voidIt = async (e) => {
    const reason = window.prompt("Reason for voiding this expense:");
    if (!reason) return;
    try { await api.post(`/expenses/${e.id}/void`, { reason }); toast.success("Expense voided"); reload(); } catch (err) { toast.error(errMsg(err)); }
  };
  return (
    <div className="content">
      <PageHeading eyebrow="EXPENSES / BILLS" title="Clinic spending," accent="with the bill attached." sub="Snap a bill, check the suggested details, save. Cash expenses from the drawer flow into the cashbook.">
        <input type="month" className="date-input" value={month} onChange={(e) => e.target.value && setMonth(e.target.value)} data-testid="expenses-month-input" />
        {can("expenses:write") && <button className="primary" onClick={() => setShow(true)} data-testid="add-expense-button"><Plus size={18} /> Log expense</button>}
      </PageHeading>
      <section className="summary-grid four">
        <div className="mini-stat strong"><span>Total this month</span><b data-testid="expenses-total">{inr(total)}</b></div>
        <div className="mini-stat"><span>From cash drawer</span><b className="out" data-testid="expenses-cash-total">{inr(cash)}</b></div>
        {byCat.slice(0, 2).map(([c, v]) => <div className="mini-stat" key={c}><span>{c}</span><b>{inr(v)}</b></div>)}
      </section>
      <ErrorBox error={error} testid="expenses-error" />
      <section className="table-card">
        {data.length ? data.map((e) => (
          <div className={`cash-row ${e.status === "void" ? "voided" : ""}`} key={e.id} data-testid={`expense-row-${e.id}`}>
            <span className="cash-dir out"><Receipt size={14} /></span>
            <div className="cash-label"><b>{e.vendor || e.description}</b><small>{e.date} · {e.category} · {e.method}{e.from_drawer ? " · from drawer" : ""}{e.vendor && ` · ${e.description}`}{e.void_reason && ` · void: ${e.void_reason}`}</small>{e.bill_file_id && <BillLink fileId={e.bill_file_id} />}</div>
            <span className="amount due" data-testid={`expense-amount-${e.id}`}>{inr(e.amount)}</span>
            {can("payments:void") && e.status === "active" ? <button className="icon-btn" onClick={() => voidIt(e)} title="Void" data-testid={`void-expense-${e.id}`}><Ban size={15} /></button> : <span />}
          </div>
        )) : <Empty icon={Receipt} title="No expenses this month" text="Log an expense with or without a bill photo." testid="expenses-empty" />}
      </section>
      {show && <ExpenseForm onClose={() => setShow(false)} onSaved={() => { setShow(false); reload(); }} />}
    </div>
  );
}
